import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { createSkillAgentMoodboardFromMeeting } from "../../api/Skills/skillAgentMoodboardsApi";
import {
  sortTemporadasByYearThenAlpha,
  temporadaFromMeetingTitle,
} from "./meetingTemporadaSort";
import {
  matchClientOption,
  optionsFromCustomers,
} from "./meetingMoodboardClientOptions";

function isImagePhoto(photo) {
  const mime = String(photo?.mimeType || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  if (mime.startsWith("video/")) return false;
  const name = String(
    photo?.originalFileName || photo?.fileUrl || "",
  ).toLowerCase();
  return /\.(jpe?g|png|webp|gif|bmp)$/i.test(name);
}

function isVideoPhoto(photo) {
  const mime = String(photo?.mimeType || "").toLowerCase();
  if (mime.startsWith("video/")) return true;
  if (mime.startsWith("image/")) return false;
  const name = String(
    photo?.originalFileName || photo?.fileUrl || "",
  ).toLowerCase();
  return /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(name);
}

function defaultBoardTitle(kind, session) {
  const customerName = String(session?.customerName || "").trim();
  const meetingLabel = String(
    session?.title || customerName || "Reunión",
  ).trim();
  const prefix = kind === "resumen" ? "Recap" : "Moodboard";
  return `${prefix} · ${meetingLabel}`.slice(0, 240);
}

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const pop = keyframes`
  0%, 10% { opacity: 0; transform: translateY(8px) scale(0.96); }
  28%, 72% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(4px) scale(0.98); }
`;

const wash = keyframes`
  0%, 12% { opacity: 0.35; transform: scale(0.92); }
  40%, 70% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.4; transform: scale(0.95); }
`;

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        overflow: "hidden",
        bgcolor: tint,
      }}
    >
      {children}
    </Box>
  );
}

function AviatorPreview() {
  return (
    <Stage tint="#f3efe8">
      <Box
        sx={{
          position: "absolute",
          left: 10,
          top: 10,
          bottom: 10,
          width: 34,
          bgcolor: "#1e293b",
          borderRadius: 0.75,
          animation: `${pop} 4.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      {[0, 1, 2].map((col) =>
        [0, 1].map((row) => (
          <Box
            key={`${col}-${row}`}
            sx={{
              position: "absolute",
              left: 50 + col * 28,
              top: 10 + row * 36,
              width: 24,
              height: 30,
              bgcolor: ["#0f766e", "#44403c", "#a16207"][col],
              borderRadius: 0.5,
              animation: `${pop} 4.6s ease-in-out infinite`,
              animationDelay: `${0.12 + col * 0.1 + row * 0.08}s`,
              ...motionSafe,
            }}
          />
        )),
      )}
    </Stage>
  );
}

function AiPreview() {
  const blocks = [
    { left: 8, top: 8, w: 52, h: 72, c: "#0f766e", d: 0 },
    { left: 66, top: 8, w: 40, h: 34, c: "#1e293b", d: 0.15 },
    { left: 66, top: 46, w: 18, h: 34, c: "#a16207", d: 0.25 },
    { left: 88, top: 46, w: 18, h: 34, c: "#44403c", d: 0.35 },
  ];
  return (
    <Stage tint="#eef6f4">
      {blocks.map((b) => (
        <Box
          key={`${b.left}-${b.top}`}
          sx={{
            position: "absolute",
            left: b.left,
            top: b.top,
            width: b.w,
            height: b.h,
            bgcolor: b.c,
            borderRadius: 1,
            animation: `${wash} 4.8s ease-in-out infinite`,
            animationDelay: `${b.d}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Stage>
  );
}

function TemplateCard({ id, selected, onSelect, title, body, accent }) {
  const theme = useTheme();
  const Preview = id === "ai" ? AiPreview : AviatorPreview;
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      sx={{
        p: 0,
        textAlign: "left",
        cursor: "pointer",
        borderRadius: 2.5,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? accent : "divider",
        bgcolor: "background.paper",
        boxShadow: selected ? `0 8px 22px ${alpha(accent, 0.16)}` : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        font: "inherit",
        color: "inherit",
        flex: "1 1 160px",
        minWidth: 0,
        "&:hover": { borderColor: selected ? accent : alpha(accent, 0.45) },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Preview />
        {selected ? (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              color: accent,
              bgcolor: "#fff",
              borderRadius: "50%",
              fontSize: 20,
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ px: 1.25, py: 1.1 }}>
        <Typography fontWeight={800} fontSize="0.92rem">
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Dialog: crear Moodboard o Recap desde medias de reunión.
 */
export default function MeetingCreateMoodboardDialog({
  open,
  onClose,
  session,
  selectedPhotos = [],
  onCreated,
  /** Rama inicial: moodboard | resumen (Recap). */
  defaultBranch = "resumen",
}) {
  const { t } = useTranslation();
  const sessionId = Number(session?.id) || 0;
  const openedForRef = useRef(null);
  const catalogsRef = useRef({ temporadas: null, options: null });
  const initialBranch = defaultBranch === "moodboard" ? "moodboard" : "resumen";

  const [catalogsReady, setCatalogsReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [branch, setBranch] = useState(initialBranch);
  const [layoutTemplate, setLayoutTemplate] = useState("aviator");
  const [temporadas, setTemporadas] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [titulo, setTitulo] = useState("");
  const [temporada, setTemporada] = useState("");
  const [cliente, setCliente] = useState(null);
  const [groupPages, setGroupPages] = useState(true);
  const [labelCliente, setLabelCliente] = useState(true);

  const isResumen = branch === "resumen";

  const namedGroups = useMemo(
    () =>
      (Array.isArray(session?.mediaGroups) ? session.mediaGroups : []).filter(
        (g) => Array.isArray(g?.mediaIds) && g.mediaIds.length,
      ),
    [session],
  );
  const hasGroups = namedGroups.length > 0;

  const boardPhotos = useMemo(
    () =>
      (Array.isArray(selectedPhotos) ? selectedPhotos : []).filter(
        (p) => isImagePhoto(p) || isVideoPhoto(p),
      ),
    [selectedPhotos],
  );
  const videoCount = useMemo(
    () => boardPhotos.filter(isVideoPhoto).length,
    [boardPhotos],
  );

  useEffect(() => {
    if (!open) return undefined;
    if (catalogsRef.current.temporadas && catalogsRef.current.options) {
      setTemporadas(catalogsRef.current.temporadas);
      setCustomerOptions(catalogsRef.current.options);
      setCatalogsReady(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [temps, customersData] = await Promise.all([
          getTemporadas(),
          getCustomers(),
        ]);
        if (cancelled) return;
        const tempList = sortTemporadasByYearThenAlpha(
          Array.isArray(temps) ? temps : [],
        );
        const options = optionsFromCustomers(
          Array.isArray(customersData) ? customersData : [],
        );
        catalogsRef.current = { temporadas: tempList, options };
        setTemporadas(tempList);
        setCustomerOptions(options);
        setCatalogsReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error || t("Meetings.moodboardLoadError"),
          );
          setCatalogsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  useEffect(() => {
    if (!open || !sessionId) return;
    if (openedForRef.current === sessionId) return;
    openedForRef.current = sessionId;
    setError("");
    setSaving(false);
    setBranch(initialBranch);
    setLayoutTemplate("aviator");
    setTemporada("");
    setGroupPages(
      (Array.isArray(session?.mediaGroups) ? session.mediaGroups : []).some(
        (g) => Array.isArray(g?.mediaIds) && g.mediaIds.length,
      ),
    );
    setLabelCliente(true);
    setTitulo(defaultBoardTitle(initialBranch, session));
    const seasonList = catalogsRef.current.temporadas || temporadas;
    setTemporada(temporadaFromMeetingTitle(session?.title, seasonList));
    const options = catalogsRef.current.options || customerOptions;
    setCliente(matchClientOption(session, options));
  }, [open, sessionId, session, customerOptions, temporadas, initialBranch]);

  useEffect(() => {
    if (!open) openedForRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open || !catalogsReady || cliente || !customerOptions.length) return;
    setCliente(matchClientOption(session, customerOptions));
  }, [open, catalogsReady, customerOptions, cliente, session]);

  useEffect(() => {
    if (!open || !catalogsReady) return;
    setTemporada((current) => {
      if (current) return current;
      return temporadaFromMeetingTitle(session?.title, temporadas);
    });
  }, [open, catalogsReady, temporadas, session]);

  const handleBranchChange = (_e, next) => {
    if (!next || next === branch) return;
    setBranch(next);
    setTitulo((prev) => {
      const trimmed = String(prev || "").trim();
      const resumenPrefixes =
        branch === "resumen" ? ["Recap", "Rapport", "Resumen"] : [];
      const moodboardPrefixes = branch === "moodboard" ? ["Moodboard"] : [];
      const isDefaultish =
        !trimmed ||
        trimmed === defaultBoardTitle(branch, session) ||
        [...resumenPrefixes, ...moodboardPrefixes].some((p) =>
          trimmed.startsWith(`${p} ·`),
        );
      if (isDefaultish) return defaultBoardTitle(next, session);
      return prev;
    });
  };

  const handleCreate = async () => {
    if (!boardPhotos.length) {
      setError(t("Meetings.moodboardNeedPhotos"));
      return;
    }
    if (!String(titulo || "").trim()) {
      setError(t("Meetings.moodboardNeedTitle"));
      return;
    }
    if (!temporada) {
      setError(t("Meetings.moodboardNeedSeason"));
      return;
    }
    if (!cliente?.id) {
      setError(t("Meetings.moodboardNeedClient"));
      return;
    }
    const allowed = customerOptions.some(
      (o) => Number(o.id) === Number(cliente.id),
    );
    if (!allowed) {
      setError(t("Meetings.moodboardNeedClient"));
      return;
    }
    setSaving(true);
    setError("");
    const photoIds = boardPhotos.map((p) => Number(p.id)).filter((n) => n > 0);
    try {
      const created = await createSkillAgentMoodboardFromMeeting({
        sessionId,
        photoIds,
        Titulo: String(titulo).trim(),
        Temporada: temporada,
        CustomerId: Number(cliente.id),
        CategoryLabel: String(titulo).trim(),
        Kind: isResumen ? "resumen" : "moodboard",
        groupPages: Boolean(hasGroups && groupPages),
        labelCliente: Boolean(labelCliente),
        layoutTemplate,
      });
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          (isResumen
            ? t("Meetings.resumenCreateError")
            : t("Meetings.moodboardCreateError")),
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: (th) => alpha(th.palette.background.default, 0.55),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose?.()}
      maxWidth="sm"
      fullWidth
      keepMounted={false}
      transitionDuration={160}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Box
        sx={{
          px: 2.75,
          pt: 2.5,
          pb: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.1)} 0%, ${alpha(
              th.palette.info.main,
              0.06,
            )} 100%)`,
        }}
      >
        <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: 1 }}>
          {t("Meetings.title")}
        </Typography>
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ lineHeight: 1.2, mt: 0.25 }}
        >
          {isResumen
            ? t("Meetings.resumenTitle")
            : t("Meetings.moodboardTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {isResumen
            ? t("Meetings.resumenBody", { count: boardPhotos.length })
            : t("Meetings.moodboardBody", { count: boardPhotos.length })}
          {videoCount > 0
            ? ` ${t("Meetings.moodboardIncludeVideos", { count: videoCount })}`
            : ""}
        </Typography>
      </Box>

      <Box sx={{ px: 2.75, pt: 2, pb: 1, maxHeight: "70vh", overflow: "auto" }}>
        <Stack spacing={2}>
          {error ? (
            <Typography variant="body2" color="error" fontWeight={700}>
              {error}
            </Typography>
          ) : null}

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 800, display: "block", mb: 0.75 }}
            >
              {t("Meetings.boardBranchLabel")}
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={branch}
              onChange={handleBranchChange}
              disabled={saving}
              aria-label={t("Meetings.boardBranchLabel")}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 800,
                  py: 1,
                  borderRadius: 2,
                },
              }}
            >
              <ToggleButton value="moodboard" aria-label="Moodboard">
                <DashboardCustomizeOutlinedIcon
                  sx={{ fontSize: 18, mr: 0.75 }}
                />
                {t("Meetings.boardBranchMoodboard")}
              </ToggleButton>
              <ToggleButton value="resumen" aria-label="Recap">
                <SummarizeOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />
                {t("Meetings.boardBranchResumen")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 800, display: "block", mb: 0.75 }}
            >
              {t("Meetings.layoutTemplateLabel")}
            </Typography>
            <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
              <TemplateCard
                id="aviator"
                accent="#0f766e"
                selected={layoutTemplate === "aviator"}
                onSelect={() => setLayoutTemplate("aviator")}
                title={t("Meetings.layoutTemplateAviator")}
                body={t("Meetings.layoutTemplateAviatorHint")}
              />
              <TemplateCard
                id="ai"
                accent="#9a3412"
                selected={layoutTemplate === "ai"}
                onSelect={() => setLayoutTemplate("ai")}
                title={t("Meetings.layoutTemplateAi")}
                body={t("Meetings.layoutTemplateAiHint")}
              />
            </Stack>
            <FormHelperText sx={{ mt: 0.75, mx: 0 }}>
              {t("Meetings.layoutTemplateA3Hint")}
            </FormHelperText>
          </Box>

          <TextField
            label={t("Meetings.moodboardName")}
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            fullWidth
            size="small"
            disabled={saving}
            sx={fieldSx}
          />
          <FormControl
            fullWidth
            size="small"
            disabled={saving || !catalogsReady}
          >
            <InputLabel id="meeting-mb-season" shrink>
              {t("Meetings.moodboardSeason")}
            </InputLabel>
            <Select
              labelId="meeting-mb-season"
              label={t("Meetings.moodboardSeason")}
              value={temporada}
              displayEmpty
              notched
              onChange={(e) => setTemporada(e.target.value)}
              sx={{
                borderRadius: 2,
                bgcolor: (th) => alpha(th.palette.background.default, 0.55),
              }}
            >
              <MenuItem value="" disabled>
                {catalogsReady
                  ? t("Meetings.moodboardSeason")
                  : t("Meetings.moodboardLoadingCatalog", {
                      defaultValue: "Cargando…",
                    })}
              </MenuItem>
              {temporadas.map((temp) => {
                const label = String(temp.TEMPORADA || "").trim();
                if (!label) return null;
                return (
                  <MenuItem key={temp.ID_TEMPORADA || label} value={label}>
                    {label}
                    {temp.ABREVIATURA ? ` (${temp.ABREVIATURA})` : ""}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Autocomplete
            options={customerOptions}
            value={cliente}
            onChange={(_, value) => setCliente(value)}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            disabled={saving || !catalogsReady}
            loading={!catalogsReady}
            autoHighlight
            clearOnBlur
            handleHomeEndKeys
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("Meetings.moodboardClient")}
                placeholder={t("Meetings.moodboardClientHint")}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            )}
          />
          {hasGroups ? (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupPages}
                    onChange={(e) => setGroupPages(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={700}>
                    {t("Meetings.moodboardGroupPages")}
                  </Typography>
                }
              />
              <FormHelperText sx={{ mt: 0, ml: 4 }}>
                {t("Meetings.moodboardGroupPagesHint")}
              </FormHelperText>
            </Box>
          ) : null}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={labelCliente}
                  onChange={(e) => setLabelCliente(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Typography variant="body2" fontWeight={700}>
                  {t("Meetings.moodboardLabelCliente")}
                </Typography>
              }
            />
            <FormHelperText sx={{ mt: 0, ml: 4 }}>
              {t("Meetings.moodboardLabelClienteHint")}
            </FormHelperText>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2.75,
          pb: 2.25,
          pt: 1.25,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.text.primary, 0.02),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => onClose?.()}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          {t("Meetings.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={saving || !boardPhotos.length}
          onClick={handleCreate}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 2,
            px: 2,
            color: "primary.contrastText",
          }}
        >
          {isResumen
            ? t("Meetings.resumenConfirm")
            : t("Meetings.moodboardConfirm")}
        </Button>
      </Box>
    </Dialog>
  );
}
